<template lang="pug">
dialog(ref='dialog' @keydown.esc.prevent="emit('close')" @mousedown="onMouseDown" @click="onBackdropClick")
    slot
</template>

<script lang="ts">
// Shared by every dialog mounted on the page; see lockBodyScroll below.
let lockedScrollY = 0;
let lockedPath = "";
let lockHolders = 0;
</script>

<script setup lang="ts">
import { onMounted, ref, watch, onUnmounted, nextTick } from "vue";

// ** 기본 모달 제외 (스크롤 가능한 모달 경우) '.modal-scroll' 클래스명 추가하여 사용 (ex. Modal.modal-scroll)

let props = defineProps({
    open: Boolean,
    // Ignore backdrop clicks. For a dialog that states a condition the reader
    // has to act on, a stray click outside should not dismiss it; it closes
    // through its own buttons (Esc still works, and lands where OK does).
    persistent: Boolean,
});

const emit = defineEmits(["close"]);
// dialog 엘리먼트 참조
const dialog = ref<HTMLDialogElement | null>(null);

let mouseDownOutside = false;

function isOutsideDialog(e: MouseEvent): boolean {
    if (!dialog.value) return false;
    const rect = dialog.value.getBoundingClientRect();
    return (
        e.clientX < rect.left ||
        e.clientX > rect.right ||
        e.clientY < rect.top ||
        e.clientY > rect.bottom
    );
}

function onMouseDown(e: MouseEvent) {
    mouseDownOutside = e.target === dialog.value && isOutsideDialog(e);
}

function onBackdropClick(e: MouseEvent) {
    if (props.persistent) {
        mouseDownOutside = false;
        return;
    }
    if (e.target === dialog.value && mouseDownOutside && isOutsideDialog(e)) {
        emit('close');
    }
    mouseDownOutside = false;
}

// iOS Safari viewport height 계산
const setViewportHeight = () => {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
};

let viewportListenersAttached = false;

// Fixing the body is what stops iOS from scrolling the page behind an open
// dialog, but it also takes the body out of flow: the document collapses to
// the viewport and the scroll offset goes with it, so the page jumped to the
// top when a dialog opened and stayed there after it closed. Parking the
// offset in a negative `top` leaves the page looking untouched, and it is put
// back on the way out.
//
// lockHolders and lockedScrollY are module scope (top of this file) because
// several dialogs can be mounted at once: only the first to lock may read the
// offset, since window.scrollY reads 0 once the body is fixed, and only the
// last to unlock may restore it. holdsLock is per instance, so a dialog that
// never opened cannot release someone else's lock when it unmounts.
let holdsLock = false;

const lockBodyScroll = () => {
    if (holdsLock) return;
    holdsLock = true;
    if (lockHolders++ > 0) return;

    lockedScrollY = window.scrollY;
    lockedPath = location.pathname;
    document.body.style.top = `-${lockedScrollY}px`;
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
};

const unlockBodyScroll = () => {
    if (!holdsLock) return;
    holdsLock = false;
    if (--lockHolders > 0) return;

    document.body.style.overflow = "";
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.height = "";
    // Not after a navigation: the route that scroll belonged to is gone, and
    // the router puts a new page at the top on its own.
    if (location.pathname === lockedPath) window.scrollTo(0, lockedScrollY);
};

const onFocusIn = () => {
    document.body.style.height = "100dvh";
    setViewportHeight();
};

const onFocusOut = () => {
    document.body.style.height = "";
    setViewportHeight();
};

const attachViewportListeners = () => {
    if (viewportListenersAttached) return;

    window.addEventListener("focusin", onFocusIn);
    window.addEventListener("focusout", onFocusOut);
    window.addEventListener("resize", setViewportHeight);
    viewportListenersAttached = true;
};

const detachViewportListeners = () => {
    if (!viewportListenersAttached) return;

    window.removeEventListener("focusin", onFocusIn);
    window.removeEventListener("focusout", onFocusOut);
    window.removeEventListener("resize", setViewportHeight);
    viewportListenersAttached = false;
};

const resetScroll = () => {
    if (!dialog.value) return;
    dialog.value.scrollTop = 0;
};

const openModal = async () => {
    attachViewportListeners();
    setViewportHeight();

    if (dialog.value && !dialog.value.open) {
        dialog.value.showModal();
    }

    await nextTick();
    requestAnimationFrame(resetScroll);
    lockBodyScroll();
};

const closeModal = () => {
    if (dialog.value?.open) {
        dialog.value.close();
    }

    unlockBodyScroll();
    detachViewportListeners();
};

watch(
    () => props.open,
    async (nv) => {
        if (nv) {
            await openModal();
        } else {
            closeModal();
        }
    }
);

onMounted(async () => {
    if (props.open) {
        await openModal();
    } else {
        setViewportHeight();
    }
});

onUnmounted(() => {
    closeModal();
});
</script>

<style lang="less">
@import "@/assets/less/bq-tokens.less";
dialog,
.dialog {
    border-radius: 0;
    border: 1px solid @form-line;
    outline: none;
    box-shadow: 0 4px 24px var(--bq-shadow);
    background-color: @paper;
    color: @ink;
    padding: 3rem;

    overscroll-behavior: contain;
    touch-action: manipulation;
    -webkit-overflow-scrolling: touch;

    &::backdrop {
        background-color: rgba(0, 0, 0, 0.5);
    }
}

@media (max-width: 800px) {
    dialog,
    .dialog {
        padding: 0;
    }
}

@media (max-width: 430px) {
    dialog,
    .dialog {
        max-width: calc(100% - 1rem);
        max-height: calc(100% - 1rem);
    }
}

/* On-screen keyboard up (code/keyboard-inset.ts). A dialog is centred in the
   full height of the page, so with a keyboard open its lower half, usually the
   buttons, is behind the keyboard and there is nothing to scroll. Pin it to
   the top of the area still on screen and cap it at that area's height: the
   dialog then scrolls inside itself and every field and button can be reached.
   `--vv-top` follows the browser when it pans the page to show a field. */
html.kb-open dialog[open] {
    top: calc(var(--vv-top, 0px) + 0.5rem);
    bottom: auto;
    margin-top: 0;
    margin-bottom: 0;
    max-height: calc(var(--vvh, 100vh) - 1rem);
    overflow-y: auto;
}
</style>
